import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SeoHead from '@/components/base/SeoHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { ArticleSchema, BreadcrumbListSchema, WebPageSchema } from '@/components/base/SchemaOrg';
import { resources } from '@/mocks/resources';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = resources.find((r) => r.id === Number(id));

  if (!article) {
    return (
      <Layout>
        <div className="pt-24 pb-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">资源未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的资源可能已被移除或链接失效</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const relatedResources = resources
    .filter((r) => r.categorySlug === article.categorySlug && r.id !== article.id)
    .slice(0, 3);

  const baseUrl = 'https://aialhku.cn';

  return (
    <Layout>
      <SeoHead
        title={`${article.title} - 无码专区`}
        description={article.description.substring(0, 150)}
        keywords={`${article.tags.join(',')},无码专区,无码专区高清资源,无码专区免费下载,无码专区资源导航`}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
      />
      <ArticleSchema
        headline={article.title}
        description={article.description}
        url={`${baseUrl}/article/${article.id}`}
        image={article.image}
        datePublished={article.date}
        dateModified={article.date}
        category={article.category}
      />
      <WebPageSchema
        name={article.title}
        description={article.description.substring(0, 150)}
        url={`${baseUrl}/article/${article.id}`}
        datePublished={article.date}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', item: baseUrl + '/' },
          { name: article.category, item: `${baseUrl}/category/${article.categorySlug}` },
          { name: article.title, item: `${baseUrl}/article/${article.id}` },
        ]}
      />

      <article className="pt-20 pb-14 md:pb-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <div className="mt-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-6 flex-wrap">
              <span className="px-2.5 py-1 bg-accent-100 text-accent-700 rounded-full font-medium whitespace-nowrap">
                {article.category}
              </span>
              <span><i className="ri-calendar-line mr-1"></i>发布于 {article.date}</span>
              <span><i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()} 次浏览</span>
              <span><i className="ri-download-line mr-1"></i>{article.downloads.toLocaleString()} 次下载</span>
            </div>
          </div>

          <div className="w-full rounded-xl overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 无码专区高清资源`}
              className="w-full h-auto object-cover object-top"
            />
          </div>

          <div
            className="prose prose-sm max-w-none text-foreground-700 leading-relaxed mb-10"
            dangerouslySetInnerHTML={{ __html: article.contentHtml }}
          />

          <div className="flex items-center gap-2 flex-wrap mb-10 pt-6 border-t border-background-200/70">
            <span className="text-sm text-foreground-500">标签：</span>
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 py-8 bg-background-100 rounded-xl mb-12">
            <button className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-download-line mr-2"></i>免费下载资源
            </button>
            <button className="px-6 py-2.5 border border-background-300/60 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-bookmark-line mr-2"></i>收藏资源
            </button>
          </div>

          {relatedResources.length > 0 && (
            <div>
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-5">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedResources.map((r) => (
                  <Link
                    key={r.id}
                    to={`/article/${r.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="w-full h-36 overflow-hidden">
                      <img
                        src={r.image}
                        alt={r.title}
                        title={`${r.title} - 无码专区相关推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-heading text-xs font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {r.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </Layout>
  );
}